/**
 * @file Wire schemas for the admin Courses panel — `installed_courses`
 *       CRUD + the historical refresh route.
 * @module @labcraft/admin/shared/courses_routes
 *
 * Admin owns these route schemas as of Part 5 of #693 (the admin↔lab
 * decouple). They previously lived in `@labcraft/lab/routes` and
 * admin imported them; after the decouple, admin writes
 * `installed_courses` directly via `@labcraft/postgres` and lab no
 * longer participates in the operator-tool write path.
 *
 * The route paths stay `/api/courses/*` so the existing admin SPA +
 * curl scripts keep working unchanged. The wire shapes match what
 * lab's routes used to publish.
 */

import { defineRoute } from '@labcraft/core'
import { z } from 'zod'

////////////////////////////////////////////////////////////////////////////////
// One installed-courses row, wire-shaped (ISO strings instead of Date)
////////////////////////////////////////////////////////////////////////////////

/**
 * One `installed_courses` row as it appears on the wire.
 *
 * @property courseId - course slug (table primary key)
 * @property version - operator-assigned version string
 * @property sourcePath - relative path under `LC_LAB_COURSES_DIR`
 *   pointing at the course's artifact tree
 * @property installedAt - ISO-8601 timestamp of row creation
 * @property enabledAt - ISO-8601 timestamp of last enable, or `null`
 *   when installed-but-disabled
 */
export const InstalledCourseSchema = z.object({
  courseId: z.string().min(1),
  version: z.string().min(1),
  sourcePath: z.string().min(1),
  installedAt: z.string().min(1),
  enabledAt: z.string().nullable(),
})

/** Parsed `InstalledCourse`. */
export type InstalledCourse = z.infer<typeof InstalledCourseSchema>

////////////////////////////////////////////////////////////////////////////////
// CRUD routes
////////////////////////////////////////////////////////////////////////////////

/**
 * `GET /api/courses/installed` — list every installed course (enabled
 * + disabled). Admin Courses panel reads this.
 */
export const GetInstalledCoursesRoute = defineRoute({
  method: 'GET',
  path: '/api/courses/installed',
  response: z.array(InstalledCourseSchema),
  description: 'List every installed course (enabled + disabled).',
})

/**
 * Body of `POST /api/courses/installed`. Operator names the course id
 * + version + source path; admin inserts the row and (by default)
 * marks it enabled. The `lc_courses` LISTEN/NOTIFY trigger fires on
 * insert so lab's registry refreshes without admin having to call lab.
 *
 * @property courseId - course slug; must match the manifest under
 *   `<LC_LAB_COURSES_DIR>/<sourcePath>/`
 * @property version - free-form operator-assigned version string
 * @property sourcePath - relative path under `LC_LAB_COURSES_DIR`;
 *   must not contain `..` or start with `/`
 * @property enabled - whether the row is enabled on insert (default true)
 */
export const InstallCourseRequestSchema = z.object({
  courseId: z.string().min(1),
  version: z.string().min(1),
  sourcePath: z.string().min(1),
  enabled: z.boolean().optional(),
})

/** Parsed `InstallCourseRequest`. */
export type InstallCourseRequest = z.infer<typeof InstallCourseRequestSchema>

/**
 * `POST /api/courses/installed` — install a course. Inserts a row
 * directly into `installed_courses`; the LISTEN/NOTIFY trigger pushes
 * the change to lab.
 */
export const PostInstalledCourseRoute = defineRoute({
  method: 'POST',
  path: '/api/courses/installed',
  request: InstallCourseRequestSchema,
  response: InstalledCourseSchema,
  description: 'Install (insert) a course row.',
})

/**
 * Body of `PATCH /api/courses/installed/:courseId`. Every field is
 * optional — empty patch is a no-op load that returns the existing
 * row unchanged.
 */
export const UpdateInstalledCourseRequestSchema = z.object({
  enabled: z.boolean().optional(),
  version: z.string().min(1).optional(),
  sourcePath: z.string().min(1).optional(),
})

/** Parsed `UpdateInstalledCourseRequest`. */
export type UpdateInstalledCourseRequest = z.infer<
  typeof UpdateInstalledCourseRequestSchema
>

/**
 * `PATCH /api/courses/installed/:courseId` — toggle enabled, change
 * version, or change source path. Returns the updated row (404 when
 * no row matched).
 */
export const PatchInstalledCourseRoute = defineRoute({
  method: 'PATCH',
  path: '/api/courses/installed/:courseId',
  request: UpdateInstalledCourseRequestSchema,
  response: InstalledCourseSchema,
  description: 'Patch one installed-course row.',
})

/**
 * `DELETE /api/courses/installed/:courseId` — uninstall (delete the
 * row). Artifact files on disk are NOT deleted; the operator is
 * responsible for cleaning the per-course subdir under
 * `LC_LAB_COURSES_DIR` separately. Returns `{ deleted: true }` on
 * success, 404 when no row matched.
 */
export const DeleteInstalledCourseRoute = defineRoute({
  method: 'DELETE',
  path: '/api/courses/installed/:courseId',
  response: z.object({ deleted: z.literal(true) }),
  description: 'Uninstall (delete) one installed-course row.',
})

/**
 * `POST /api/courses/refresh` — historically admin's "tell lab to
 * refresh" hook, kept for SPA + curl compatibility. Returns the
 * current set of course IDs from `installed_courses`; lab refreshes
 * its registry on the `lc_courses` LISTEN channel, not on this call.
 */
export const PostCoursesRefreshRoute = defineRoute({
  method: 'POST',
  path: '/api/courses/refresh',
  response: z.object({
    courseIds: z.array(z.string()),
  }),
  description: 'Enumerate installed course IDs (no-op refresh hook).',
})
